import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "sonner";

import FastAPI from "../../api/FastAPI";

import type { UseMutationResult } from "@tanstack/react-query";

import type {
  GetPlaylistResponse,
  UpdatedPlaylist,
} from "../../api/FastAPI.types";
import type { FASTChannelRoutesParams } from "../../components/routes/FASTChannelRoutes";
import type { PlaylistDropzoneMedia } from "../../types/fast";

interface UpdatePlaylistVariables {
  isNewPlaylist: boolean;
  updatedPlaylist: UpdatedPlaylist;
  mediaList?: PlaylistDropzoneMedia[];
}

interface UseFASTPlaylistMutationsParams {
  updateFASTPlaylist: UseMutationResult<
    GetPlaylistResponse,
    unknown,
    UpdatePlaylistVariables,
    GetPlaylistResponse
  >;
}
export default function useFASTPlaylistMutations(): UseFASTPlaylistMutationsParams {
  const history = useHistory();
  const { t } = useTranslation("editor-fast-channel");

  const { channelId, playlistId } = useParams<
    FASTChannelRoutesParams & {
      playlistId: string;
    }
  >();
  const queryClient = useQueryClient();

  const getPlaylistQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylist,
    params: [
      {
        channelId,
        playlistId,
      },
    ],
  });

  const getPlaylistListQuery = FastAPI.generateQuery({
    fn: FastAPI.getPlaylistList,
    params: [channelId],
  });

  const updateFASTPlaylist = useMutation({
    mutationFn: async ({
      updatedPlaylist,
      isNewPlaylist,
    }: UpdatePlaylistVariables) =>
      isNewPlaylist
        ? FastAPI.createPlaylist({
            channelId,
            updatedPlaylist,
          })
        : FastAPI.updatePlaylist({
            channelId,
            playlistId,
            updatedPlaylist,
          }),
    onMutate: ({
      mediaList,
      isNewPlaylist,
      updatedPlaylist,
    }): GetPlaylistResponse | undefined => {
      if (isNewPlaylist) return undefined;

      queryClient.cancelQueries(getPlaylistQuery.queryKey);

      const previousPlaylist = queryClient.getQueryData<GetPlaylistResponse>(
        getPlaylistQuery.queryKey,
      );

      queryClient.setQueryData(getPlaylistQuery.queryKey, {
        ...previousPlaylist,
        name: updatedPlaylist.name,
        items: mediaList?.map((media) => ({
          label: media.label,
          rank: media.rank,
          duration: media.duration,
          format: media.formatKey,
          media_uuid: media.mediaId,
        })),
      });

      return previousPlaylist;
    },
    onSuccess: ({ uuid }, { isNewPlaylist }): void => {
      if (isNewPlaylist) {
        history.replace(uuid);
      }

      queryClient.invalidateQueries(getPlaylistListQuery.queryKey);

      toast.success(t("playlistUpdateSuccess", { ns: "editor-fast-channel" }));
    },
    onError: (_, { mediaList, isNewPlaylist }, previousPlaylist): void => {
      queryClient.setQueryData(getPlaylistQuery.queryKey, previousPlaylist);

      const noMediaMessage = !mediaList?.length
        ? t("noPlaylistMediaError", { ns: "editor-fast-channel" })
        : "";

      toast.error(
        isNewPlaylist
          ? t("playlistCreateError", {
              noMediaMessage,
              ns: "editor-fast-channel",
            })
          : t("playlistUpdateError", {
              noMediaMessage,
              ns: "editor-fast-channel",
            }),
      );
    },
  });

  return {
    updateFASTPlaylist,
  };
}
